/** @jsx jsx */
import React, { useMemo, useState, useCallback } from 'react';
import { Column } from 'react-table';
import { Link } from 'react-router-dom';
import { css, jsx } from '@emotion/core';
import PrimaryButton from 'components/atoms/Button';
import PageTitle from 'components/atoms/PageTitle';
import DataTable from 'components/organismos/DataTable/DataTable';
import LableTitle from 'components/atoms/LableTitle';
import { iconColor } from 'components/styles';
import Icon from 'components/atoms/Icon';
import ConfirmActionModal, { ActionType } from 'components/organismos/ConfirmActionModal';
import BodyText from 'components/atoms/BodyText';
import { LaborCostCustomItemMst } from 'api/laborCostCustomItemMst';

import { useLaborCostCustomItemList, useLaborCostCustomItemForm } from './hooks';

// 登録上限はサーバー側（company.labor_cost_custom_item_limit）で制御

const LaborCostFreeItemPage: React.FC<{
  roleScreen?: any;
}> = ({ roleScreen }) => {
  const { itemList, fetchList } = useLaborCostCustomItemList();
  const { handleDelete } = useLaborCostCustomItemForm(fetchList);

  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState('');

  const openConfirmModal = useCallback((customId: string) => {
    setDeleteTargetId(customId);
    setConfirmModalOpen(true);
  }, []);

  const closeConfirmModal = useCallback(() => {
    setConfirmModalOpen(false);
  }, []);

  const onDelete = useCallback(() => {
    handleDelete(deleteTargetId);
    setConfirmModalOpen(false);
  }, [deleteTargetId, handleDelete]);

  const columns: Array<Column<LaborCostCustomItemMst>> = useMemo(
    () => [
      {
        Header: '表示順',
        accessor: 'dispOrder',
        sortType: 'basic',
      },
      {
        Header: '項目名',
        accessor: 'customItemName',
      },
      {
        Header: '勤怠管理',
        id: 'includeAttend',
        Cell: (cell: { row: { original: LaborCostCustomItemMst } }) => (
          <span>{cell.row.original.includeAttend ? '人件費に含める' : '人件費に含めない'}</span>
        ),
      },
      {
        Header: '損益管理',
        id: 'includeSales',
        Cell: (cell: { row: { original: LaborCostCustomItemMst } }) => (
          <span>{cell.row.original.includeSales ? '人件費に含める' : '人件費に含めない'}</span>
        ),
      },
      {
        Header: () => null,
        id: 'actionButton',
        disableSortBy: true,
        Cell: (cell: {
          row: { original: LaborCostCustomItemMst };
        }) => {
          const [isHoverEdit, setIsHoverEdit] = useState(false);
          const [isHoverDelete, setIsHoverDelete] = useState(false);
          return (
            <div style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }}>
              <Link to={`/laborCostCustomItem/edit/${cell.row.original.customId}`}>
                <span
                  style={{ marginLeft: '20px' }}
                  onMouseEnter={() => setIsHoverEdit(true)}
                  onMouseLeave={() => setIsHoverEdit(false)}
                >
                  <Icon type="edit" color={isHoverEdit ? iconColor.grayhover : iconColor.gray} />
                </span>
              </Link>
              {roleScreen && roleScreen.editable === 1 && (
                <Link
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    openConfirmModal(cell.row.original.customId!);
                  }}
                >
                  <span
                    style={{ marginLeft: '20px', marginRight: '10px' }}
                    onMouseEnter={() => setIsHoverDelete(true)}
                    onMouseLeave={() => setIsHoverDelete(false)}
                  >
                    <Icon
                      type="delete"
                      color={isHoverDelete ? iconColor.grayhover : iconColor.gray}
                    />
                  </span>
                </Link>
              )}
            </div>
          );
        },
      },
    ],
    [openConfirmModal, roleScreen],
  );

  return (
    <React.Fragment>
      <div>
        <LableTitle title="人件費カスタム項目を登録すると、スタッフマスタの人件費設定画面に入力欄が追加されます。" />
        {roleScreen && roleScreen.editable === 1 && (
          <PageTitle
            title=""
            rightContents={
              <span style={{ marginRight: '12px' }}>
                <Link to="/laborCostCustomItem/create">
                  <PrimaryButton
                    ghost={false}
                    text="新規人件費カスタム項目作成"
                  />
                </Link>
              </span>
            }
          />
        )}
        <div
          css={css`
            table th:nth-child(1) {
              width: 80px;
            }
            table th:nth-child(2) {
              width: 200px;
            }
            table th:nth-child(3) {
              width: 60px;
            }
            table th:nth-child(4) {
              width: 60px;
            }
            table th:nth-child(5) {
              width: 80px;
            }
          `}
        >
          <div style={{ overflowX: 'auto', whiteSpace: 'nowrap', margin: '15px' }}>
            <DataTable
              columns={columns}
              data={itemList}
              sortBy={[{ id: 'dispOrder', desc: false }]}
            />
          </div>
        </div>
        <ConfirmActionModal
          open={confirmModalOpen}
          closeHandler={closeConfirmModal}
          onSubmit={onDelete}
          actionType={ActionType.DELETE}
          message=""
        />
      </div>
    </React.Fragment>
  );
};

export default LaborCostFreeItemPage;
